import { useCallback, useSyncExternalStore } from 'react';
import { z } from 'zod';
import {
  APP_HOME_SSE_KEEPALIVE_MS,
  LoopSuggestionsReadyInvalidatePayloadSchema,
  SsePayloadSchemas,
  type LoopSuggestionsReadyInvalidatePayload,
  type SseEventData,
  type SseEventName,
  type StudioCuePayload,
} from './app-home-sse-schemas';
import { logger } from './logger';
import { emitDebugEvent } from './skill-debug-events';
import { validateResponse } from './validateResponse';
import { safeParseJson, summarizeIssues } from '@shared/parse-json';

type ViewMode = 'active' | 'archived';

type StreamKey = string;

type AppHomeOpenEvent = { type: 'open' };
type AppHomeErrorEvent = { type: 'error'; reason?: string };
/**
 * Invalidate event — a discriminated union keyed by `reason`. The
 * `execution_artifact_received` variant has typed extras; other reasons
 * (including legacy/absent) use the generic variant with opaque extras.
 *
 * Because TS does not narrow a union whose variants have overlapping
 * optional fields purely via `event.reason === '...'`, use
 * `isInvalidateExecutionArtifactReceived(event)` to narrow to the typed
 * extras variant.
 */
export type AppHomeInvalidateExecutionArtifactReceivedEvent = {
  type: 'invalidate';
  reason: 'execution_artifact_received';
  extras: {
    actionName?: string;
    correlationId?: string;
    artifactCount?: number;
    executionId?: string | number;
    expectedArtifactCount?: number;
  };
};
export type AppHomeInvalidateLoopSuggestionsReadyEvent = {
  type: 'invalidate';
  reason: 'loop_suggestions_ready';
  extras: Omit<LoopSuggestionsReadyInvalidatePayload, 'reason'>;
};
type AppHomeInvalidateGenericEvent = {
  type: 'invalidate';
  reason?: string;
  extras?: Record<string, unknown>;
};
export type AppHomeInvalidateEvent =
  | AppHomeInvalidateExecutionArtifactReceivedEvent
  | AppHomeInvalidateLoopSuggestionsReadyEvent
  | AppHomeInvalidateGenericEvent;

export function isInvalidateExecutionArtifactReceived(
  event: AppHomeInvalidateEvent,
): event is AppHomeInvalidateExecutionArtifactReceivedEvent {
  return event.reason === 'execution_artifact_received';
}

export function isInvalidateLoopSuggestionsReady(
  event: AppHomeInvalidateEvent,
): event is AppHomeInvalidateLoopSuggestionsReadyEvent {
  return event.reason === 'loop_suggestions_ready';
}
type AppHomeToastEvent = {
  type: 'toast';
  message: string;
  level?: 'info' | 'warn' | 'error';
  /** Present when the toast represents a reauth prompt (oauthUrl for Reconnect action). */
  oauthUrl?: string;
  /** Stable error code, e.g. 'SF_REAUTH_REQUIRED'. */
  code?: string;
};
type AppHomeArtifactUpdatedEvent = {
  type: 'artifact:updated';
  uuid: string;
  fields: Record<string, unknown>;
};
type AppHomeArtifactCreatedEvent = { type: 'artifact:created'; uuid: string };
type AppHomeArtifactDeletedEvent = { type: 'artifact:deleted'; uuid: string };
/** The three dedicated-receipt channels (see StreamState). */
type AppHomeFlowNotesChangedEvent = {
  type: 'flow:notes:changed';
  payload: SseEventData<'flow:notes:changed'>;
};
type AppHomeBlueprintDraftUpdatedEvent = { type: 'blueprint:draft:updated'; kind: string; seq: number };
type AppHomeStudioCueEvent = { type: 'studio:cue'; payload: StudioCuePayload };
export type AppHomeStreamEvent =
  | AppHomeOpenEvent
  | AppHomeErrorEvent
  | AppHomeInvalidateEvent
  | AppHomeToastEvent
  | AppHomeArtifactUpdatedEvent
  | AppHomeArtifactCreatedEvent
  | AppHomeArtifactDeletedEvent
  | AppHomeFlowNotesChangedEvent
  | AppHomeBlueprintDraftUpdatedEvent
  | AppHomeStudioCueEvent;

/**
 * `reconnect` is true only on an `open` frame that follows a real drop: the
 * entry was open before AND then an `error` came, or the source was swapped
 * after `SSE_LIVENESS_TIMEOUT_MS` with no frame or ping. A swap of a live
 * source (tab refocus, `online`) is not a reconnect.
 */
export type AppHomeEventMeta = { reconnect: boolean };
export type AppHomeEventListener = (event: AppHomeStreamEvent, meta: AppHomeEventMeta) => void;

/**
 * Receipt for the blueprint studio truth channel (`blueprint:draft:updated`).
 *
 * `count` is a monotonic per-stream receipt counter, bumped on every accepted
 * event. Consumers (`useBlueprintDraftLive`) key their refetch effect on
 * `count` rather than on the payload, so two byte-identical frames still
 * register as two invalidations.
 */
export type BlueprintDraftUpdatedReceipt = {
  kind: string;
  seq: number;
  count: number;
};

/**
 * Receipt for the Notes live-update channel (`flow:notes:changed`). Same
 * contract as the blueprint receipt: `count` bumps on every accepted frame so
 * a consumer keys its invalidation on it, not on the (overwritable) payload.
 */
export type FlowNotesChangedReceipt = {
  payload: SseEventData<'flow:notes:changed'>;
  count: number;
  at: number;
};

/** Receipt for the blueprint studio presentation channel (`studio:cue`). */
export type StudioCueReceipt = {
  payload: StudioCuePayload;
  count: number;
};

/**
 * The React-visible state of one stream: whether it is connected, plus the
 * receipt of the last frame on each dedicated channel. It holds no event slot.
 * To react to frames, use `subscribeAppHomeEvents` (every frame, plus a
 * reconnect flag) or `useStreamInvalidation` for cache invalidation.
 *
 * The state changes only when `connected` flips or a receipt is accepted, so
 * an ordinary frame does not re-render `useAppHomeStream` consumers. Every
 * writer spreads the previous state, or it clobbers the receipts back to null.
 */
export type StreamState = {
  connected: boolean;
  lastBlueprintDraftUpdated: BlueprintDraftUpdatedReceipt | null;
  lastStudioCue: StudioCueReceipt | null;
  lastFlowNotesChanged: FlowNotesChangedReceipt | null;
};

type StoreEntry = {
  source: EventSource;
  listeners: Set<() => void>;
  /** Per-frame subscribers. They see every frame, outside React batching. */
  eventListeners: Set<AppHomeEventListener>;
  /** True after the first `open`. */
  everOpened: boolean;
  /** True from an `error` until the next `open`, so that `open` is a reconnect. */
  droppedSinceOpen: boolean;
  /** `Date.now()` of the last `open`, frame or server `ping` on the live source. */
  lastAliveAt: number;
  state: StreamState;
  backoffMs: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  cleanupFns?: Array<() => void>;
  /** Force immediate reconnection (used when acting user changes) */
  forceReconnect?: () => void;
};

/**
 * A source that has seen no frame and no server `ping` for this long is
 * treated as dead when it is swapped (tab refocus, `online`). A socket can die
 * without an `error` event (laptop sleep, a NAT entry that expired), and the
 * server does not replay frames, so the swap must resync. Two keepalive
 * intervals, so one late ping does not count as a drop.
 */
export const SSE_LIVENESS_TIMEOUT_MS = 2 * APP_HOME_SSE_KEEPALIVE_MS;

/** How many recent frames each stream keeps for `readAppHomeEventsSince`. */
export const SSE_REPLAY_BUFFER_SIZE = 256;

export type AppHomeBufferedFrame = { event: AppHomeStreamEvent; meta: AppHomeEventMeta };

/**
 * Per-stream frame sequence and the last `SSE_REPLAY_BUFFER_SIZE` frames.
 * Kept outside `StoreEntry` so it survives a released entry: a cursor taken
 * before the release still compares against the same sequence.
 */
type StreamHistory = {
  /** Sequence number of the last dispatched frame. Starts at 0. */
  seq: number;
  frames: Array<AppHomeBufferedFrame & { seq: number }>;
  /** Sequence number of the newest frame that left the buffer. */
  evictedThrough: number;
};

const registry = new Map<StreamKey, StoreEntry>();
const histories = new Map<StreamKey, StreamHistory>();

function historyFor(key: StreamKey): StreamHistory {
  let history = histories.get(key);
  if (!history) {
    history = { seq: 0, frames: [], evictedThrough: 0 };
    histories.set(key, history);
  }
  return history;
}
const DEFAULT_STATE: StreamState = Object.freeze({
  connected: false,
  lastBlueprintDraftUpdated: null,
  lastStudioCue: null,
  lastFlowNotesChanged: null,
});

function makeKey(view: ViewMode, filter: string): StreamKey {
  return `appHome:${view}|${filter || 'all'}`;
}

/**
 * Parse the SSE-protocol payload off the wire and validate against the
 * producer's schema. Returns null on invalid JSON; logs drift on schema
 * mismatch but returns the raw data (validateResponse is observational).
 *
 * This is the trust boundary for the app-home SSE stream — the only
 * place we cross from wire string to typed data.
 */
function parseSseEvent<E extends SseEventName>(
  evt: MessageEvent,
  eventName: E,
): SseEventData<E> | null {
  if (typeof evt.data !== 'string') return null;
  const decoded = safeParseJson(z.unknown(), evt.data);
  if (!decoded.success) {
    logger.warn({ err: decoded.error, eventName }, '[sse] JSON parse failed');
    return null;
  }
  // A lookup by generic key widens to the union of schemas; the key fixes the
  // output type, which TypeScript can't correlate on its own.
  const schema = SsePayloadSchemas[eventName] as unknown as z.ZodType<SseEventData<E>>;
  return validateResponse(schema, decoded.data, `sse:${eventName}`);
}

/**
 * JSON-decode step for the blueprint-studio channels. Split out from
 * `parseSseEvent` because those two channels validate with a HARD gate
 * (see `reduceBlueprintDraftUpdated` / `reduceStudioCue`) rather than
 * `validateResponse`'s observational pass-through.
 *
 * Returns a box (not the bare value) so a legitimate `null` frame is
 * distinguishable from a decode failure.
 */
function decodeSseJson(evt: MessageEvent, eventName: SseEventName): { data: unknown } | null {
  if (typeof evt.data !== 'string') return null;
  const decoded = safeParseJson(z.unknown(), evt.data);
  if (!decoded.success) {
    logger.warn({ err: decoded.error, eventName }, '[sse] JSON parse failed');
    return null;
  }
  return { data: decoded.data };
}

/**
 * Reduce a decoded `blueprint:draft:updated` frame into the next stream state.
 *
 * Returns `null` when the frame is dropped (invalid payload) so the caller
 * knows not to publish a new snapshot or notify listeners. Exported for tests
 * — the listener body is a thin wrapper over this.
 *
 * Hard gate on purpose: `validateResponse` logs and passes the raw data
 * through, which for the studio would mean feeding a malformed frame into the
 * render path and flashing a broken card. Dropping is safe here because the
 * channel is pure invalidation — the next valid event (or the studio's
 * refetch-on-reconnect) self-heals.
 */
export function reduceBlueprintDraftUpdated(
  prev: StreamState,
  raw: unknown,
): StreamState | null {
  const parsed = SsePayloadSchemas['blueprint:draft:updated'].safeParse(raw);
  if (!parsed.success) {
    logger.warn(
      { issues: summarizeIssues(parsed.error.issues) },
      '[sse] blueprint:draft:updated dropped — invalid payload',
    );
    return null;
  }
  return {
    ...prev,
    connected: true,
    lastBlueprintDraftUpdated: {
      kind: parsed.data.kind,
      seq: parsed.data.seq,
      count: (prev.lastBlueprintDraftUpdated?.count ?? 0) + 1,
    },
  };
}

/**
 * Reduce a decoded `studio:cue` frame into the next stream state.
 *
 * Same hard gate as the truth channel, for a stronger reason: the cue is what
 * the studio renders (focus highlight, checklist badges, ghost proposal).
 * Cues are full-state and last-write-wins, so dropping one costs nothing —
 * rendering a half-valid one costs the never-flash-broken invariant.
 *
 * `proposal.blueprint` is intentionally opaque here (`Record<string,
 * unknown>`); the studio's render path re-validates it against the real
 * blueprint schema before rendering.
 */
export function reduceStudioCue(prev: StreamState, raw: unknown): StreamState | null {
  const parsed = SsePayloadSchemas['studio:cue'].safeParse(raw);
  if (!parsed.success) {
    logger.warn(
      { issues: summarizeIssues(parsed.error.issues) },
      '[sse] studio:cue dropped — invalid payload',
    );
    return null;
  }
  return {
    ...prev,
    connected: true,
    lastStudioCue: {
      payload: parsed.data,
      count: (prev.lastStudioCue?.count ?? 0) + 1,
    },
  };
}

/**
 * Reduce a decoded `flow:notes:changed` frame into the next stream state.
 * Returns `null` when the frame is dropped (invalid payload). Hard gate, same
 * as the studio channels: the channel is pure invalidation, so dropping a bad
 * frame self-heals on the next valid one or on reconnect.
 */
export function reduceFlowNotesChanged(prev: StreamState, raw: unknown): StreamState | null {
  const parsed = SsePayloadSchemas['flow:notes:changed'].safeParse(raw);
  if (!parsed.success) {
    logger.warn(
      { issues: summarizeIssues(parsed.error.issues) },
      '[sse] flow:notes:changed dropped — invalid payload',
    );
    return null;
  }
  return {
    ...prev,
    connected: true,
    lastFlowNotesChanged: {
      payload: parsed.data,
      count: (prev.lastFlowNotesChanged?.count ?? 0) + 1,
      at: Date.now(),
    },
  };
}

/**
 * `null` is a frame that failed JSON decode; consumers still get a bare
 * `invalidate` so they can react.
 *
 * A malformed `loop_suggestions_ready` loses its reason, so it is a generic
 * invalidate and never satisfies the typed guard. A malformed
 * `execution_artifact_received` keeps its reason: that is the long-standing
 * behavior and the toast consumer tolerates missing extras.
 */
function toInvalidateEvent(payload: SseEventData<'invalidate'> | null): AppHomeInvalidateEvent {
  if (!payload) return { type: 'invalidate' };
  const { reason, ...extras } = payload;
  if (reason === 'execution_artifact_received') {
    return { type: 'invalidate', reason, extras };
  }
  if (reason === 'loop_suggestions_ready') {
    const parsed = LoopSuggestionsReadyInvalidatePayloadSchema.safeParse(payload);
    if (parsed.success) {
      const { reason: typedReason, ...typedExtras } = parsed.data;
      return { type: 'invalidate', reason: typedReason, extras: typedExtras };
    }
    return { type: 'invalidate', ...(Object.keys(extras).length > 0 ? { extras } : {}) };
  }
  return {
    type: 'invalidate',
    ...(reason !== undefined ? { reason } : {}),
    ...(Object.keys(extras).length > 0 ? { extras } : {}),
  };
}

function createOrGetEntry(view: ViewMode, filter: string): StoreEntry {
  const key = makeKey(view, filter);
  const existing = registry.get(key);
  if (existing) return existing;

  const listeners = new Set<() => void>();
  const entry: StoreEntry = {
    // temporary, replaced immediately in setupSource
    source: new EventSource('/api/v4/stream?noop=1'),
    listeners,
    eventListeners: new Set(),
    everOpened: false,
    droppedSinceOpen: false,
    lastAliveAt: 0,
    state: {
      connected: false,
      lastBlueprintDraftUpdated: null,
      lastStudioCue: null,
      lastFlowNotesChanged: null,
    },
    backoffMs: 1000,
    reconnectTimer: null,
    cleanupFns: [],
  };

  function buildUrl(): string {
    // Impersonation is resolved server-side from the session — no client params needed.
    const params = new URLSearchParams({ view, filter });
    return `/api/v4/stream?` + params.toString();
  }

  function scheduleReconnect() {
    // If no subscribers left, do not reconnect
    if (!hasSubscribers(entry)) return;
    // Cap the backoff to 20s
    const delay = Math.min(entry.backoffMs, 20_000);
    logger.debug({ delay }, 'sse: reconnect scheduled');
    if (entry.reconnectTimer) clearTimeout(entry.reconnectTimer);
    entry.reconnectTimer = setTimeout(() => {
      entry.reconnectTimer = null;
      setupSource();
      // exponential backoff with jitter
      const next = Math.min(delay * 2, 20_000);
      entry.backoffMs = next + Math.floor(Math.random() * 250);
    }, delay);
  }

  /** Emit an app-home SSE event to the debug HUD (dev only) */
  function debugSSE(eventType: string, data: unknown) {
    if (import.meta.env.DEV) {
      emitDebugEvent({ timestamp: Date.now(), type: 'app_home_sse', source: 'app_home', data: { event: eventType, ...((data && typeof data === 'object') ? data : { raw: data }) } });
    }
  }

  /**
   * Fan one frame out to every per-frame subscriber. Runs synchronously in the
   * EventSource handler, so two frames in one network chunk are two calls.
   */
  function dispatch(event: AppHomeStreamEvent, meta: AppHomeEventMeta = { reconnect: false }) {
    if (event.type !== 'error') entry.lastAliveAt = Date.now();
    const history = historyFor(key);
    history.seq += 1;
    history.frames.push({ seq: history.seq, event, meta });
    if (history.frames.length > SSE_REPLAY_BUFFER_SIZE) {
      history.evictedThrough = history.frames.shift()?.seq ?? history.evictedThrough;
    }
    // Iterate a copy: a listener added during this dispatch must not get this frame.
    for (const listener of [...entry.eventListeners]) {
      try {
        listener(event, meta);
      } catch (err) {
        logger.warn({ err, eventType: event.type }, '[sse] event listener threw');
      }
    }
  }

  /** Publish a new snapshot only when `connected` flips. */
  function setConnected(connected: boolean) {
    if (entry.state.connected === connected) return;
    entry.state = { ...entry.state, connected };
    listeners.forEach((l) => l());
  }

  function setupSource() {
    // A swap of a source that went silent past the timeout is a drop the
    // browser never reported, so the next `open` resyncs.
    if (entry.everOpened && Date.now() - entry.lastAliveAt > SSE_LIVENESS_TIMEOUT_MS) {
      entry.droppedSinceOpen = true;
    }
    try { entry.source.close(); } catch {}
    const url = buildUrl();
    const es = new EventSource(url);
    entry.source = es;
    logger.debug({ key, url }, 'sse: create');

    es.addEventListener('open', () => {
      entry.backoffMs = 1000; // reset backoff on success
      debugSSE('open', { status: 'connected' });
      setConnected(true);
      const reconnect = entry.everOpened && entry.droppedSinceOpen;
      entry.everOpened = true;
      entry.droppedSinceOpen = false;
      dispatch({ type: 'open' }, { reconnect });
    });
    es.addEventListener('error', () => {
      debugSSE('error', { status: 'disconnected' });
      setConnected(false);
      entry.droppedSinceOpen = true;
      dispatch({ type: 'error' });
      scheduleReconnect();
    });
    // The server's keepalive. Liveness only: no dispatch, no snapshot.
    es.addEventListener('ping', () => {
      entry.lastAliveAt = Date.now();
    });
    es.addEventListener('toast', (evt: MessageEvent) => {
      const payload = parseSseEvent(evt, 'toast');
      if (!payload) return;
      const event: AppHomeToastEvent = {
        type: 'toast',
        message: payload.message,
        level: payload.level,
        oauthUrl: payload.oauthUrl,
        code: payload.code,
      };
      debugSSE('toast', payload);
      setConnected(true);
      dispatch(event);
    });
    es.addEventListener('invalidate', (evt: MessageEvent) => {
      const payload = parseSseEvent(evt, 'invalidate');
      const next = toInvalidateEvent(payload);
      debugSSE('invalidate', payload ?? {});
      setConnected(true);
      dispatch(next);
    });
    es.addEventListener('artifact:updated', (evt: MessageEvent) => {
      const payload = parseSseEvent(evt, 'artifact:updated');
      if (!payload) return;
      const { uuid, ...fields } = payload;
      const event: AppHomeArtifactUpdatedEvent = { type: 'artifact:updated', uuid, fields };
      debugSSE('artifact:updated', { uuid, fields: Object.keys(fields) });
      setConnected(true);
      dispatch(event);
    });
    es.addEventListener('artifact:created', (evt: MessageEvent) => {
      const payload = parseSseEvent(evt, 'artifact:created');
      if (!payload) return;
      const event: AppHomeArtifactCreatedEvent = { type: 'artifact:created', uuid: payload.uuid };
      debugSSE('artifact:created', { uuid: payload.uuid });
      setConnected(true);
      dispatch(event);
    });
    es.addEventListener('artifact:deleted', (evt: MessageEvent) => {
      const payload = parseSseEvent(evt, 'artifact:deleted');
      if (!payload) return;
      const event: AppHomeArtifactDeletedEvent = { type: 'artifact:deleted', uuid: payload.uuid };
      debugSSE('artifact:deleted', { uuid: payload.uuid });
      setConnected(true);
      dispatch(event);
    });
    // Blueprint studio truth channel (plan §3). Writes its dedicated receipt.
    es.addEventListener('blueprint:draft:updated', (evt: MessageEvent) => {
      const decoded = decodeSseJson(evt, 'blueprint:draft:updated');
      if (!decoded) return;
      const next = reduceBlueprintDraftUpdated(entry.state, decoded.data);
      if (!next) return;
      entry.state = next;
      debugSSE('blueprint:draft:updated', next.lastBlueprintDraftUpdated);
      listeners.forEach((l) => l());
      if (next.lastBlueprintDraftUpdated) {
        const { kind, seq } = next.lastBlueprintDraftUpdated;
        dispatch({ type: 'blueprint:draft:updated', kind, seq });
      }
    });
    // Blueprint studio presentation channel (plan §3). Full-state, idempotent,
    // last-write-wins — also a dedicated receipt.
    es.addEventListener('studio:cue', (evt: MessageEvent) => {
      const decoded = decodeSseJson(evt, 'studio:cue');
      if (!decoded) return;
      const next = reduceStudioCue(entry.state, decoded.data);
      if (!next) return;
      entry.state = next;
      debugSSE('studio:cue', {
        sessionId: next.lastStudioCue?.payload.sessionId,
        kind: next.lastStudioCue?.payload.kind,
        seq: next.lastStudioCue?.payload.seq,
        step: next.lastStudioCue?.payload.step,
        count: next.lastStudioCue?.count,
      });
      listeners.forEach((l) => l());
      if (next.lastStudioCue) dispatch({ type: 'studio:cue', payload: next.lastStudioCue.payload });
    });
    // Notes live-update channel. Dedicated receipt.
    es.addEventListener('flow:notes:changed', (evt: MessageEvent) => {
      const decoded = decodeSseJson(evt, 'flow:notes:changed');
      if (!decoded) return;
      const next = reduceFlowNotesChanged(entry.state, decoded.data);
      if (!next) return;
      entry.state = next;
      debugSSE('flow:notes:changed', next.lastFlowNotesChanged?.payload);
      listeners.forEach((l) => l());
      if (next.lastFlowNotesChanged) {
        dispatch({ type: 'flow:notes:changed', payload: next.lastFlowNotesChanged.payload });
      }
    });
  }

  setupSource();

  // Store reference for forced reconnection (e.g., when acting user changes)
  entry.forceReconnect = () => {
    entry.backoffMs = 0; // Reset backoff for immediate reconnect
    if (entry.reconnectTimer) {
      try { clearTimeout(entry.reconnectTimer); } catch {}
      entry.reconnectTimer = null;
    }
    setupSource();
  };

  // Environment listeners (resume faster on online/visible). Reset the backoff first:
  // scheduleReconnect re-arms its timer, so a backoff grown during an outage would
  // otherwise restart a countdown of up to 20s instead of reconnecting now.
  try {
    const resumeNow = () => {
      entry.backoffMs = 0;
      scheduleReconnect();
    };
    const onOnline = () => { if (hasSubscribers(entry)) resumeNow(); };
    const onVisibility = () => {
      try {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          resumeNow();
        }
      } catch {}
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility, true);
    entry.cleanupFns?.push(() => { try { window.removeEventListener('online', onOnline); } catch {} });
    entry.cleanupFns?.push(() => { try { document.removeEventListener('visibilitychange', onVisibility, true); } catch {} });
  } catch {}
  registry.set(key, entry);
  return entry;
}

function hasSubscribers(entry: StoreEntry): boolean {
  return entry.listeners.size > 0 || entry.eventListeners.size > 0;
}

/** Close and forget the entry once neither kind of subscriber remains. */
function releaseIfIdle(view: ViewMode, filter: string, entry: StoreEntry): void {
  if (hasSubscribers(entry)) return;
  try { entry.source.close(); } catch {}
  if (entry.reconnectTimer) { try { clearTimeout(entry.reconnectTimer); } catch {} }
  if (Array.isArray(entry.cleanupFns)) {
    for (const fn of entry.cleanupFns) { try { fn(); } catch {} }
  }
  const key = makeKey(view, filter);
  if (registry.get(key) === entry) registry.delete(key);
}

function subscribe(view: ViewMode, filter: string, onStoreChange: () => void): () => void {
  const entry = createOrGetEntry(view, filter);
  entry.listeners.add(onStoreChange);
  return () => {
    entry.listeners.delete(onStoreChange);
    releaseIfIdle(view, filter, entry);
  };
}

/**
 * Receive EVERY frame on the stream, one call per frame, in arrival order.
 * Frames that land in one network chunk are not collapsed. `meta.reconnect` is true on an `open` that follows
 * an earlier `open` with an `error` in between, or with a silence longer than
 * `SSE_LIVENESS_TIMEOUT_MS` before a swap: the server does not replay frames,
 * so a consumer should resync then. A source swapped while live (tab refocus,
 * `online`) reports `reconnect: false`. A listener-only subscriber
 * keeps the EventSource open; the last unsubscribe of either kind closes it.
 *
 * There is no replay on subscribe. A late subscriber reads what it missed with
 * `readAppHomeEventsSince` (see `useStreamInvalidation`).
 *
 * Known gap: the last unsubscribe drops the entry, so a later subscribe starts
 * a fresh one and its first `open` reports `reconnect: false`. Frames sent while
 * no one was subscribed (a whole-tree remount) are not reported as a gap.
 *
 * Prefer `useStreamInvalidation` for cache invalidation; use this directly
 * only for side effects that are not a cache write (e.g. toasts).
 */
export function subscribeAppHomeEvents(
  { view, filter }: { view: ViewMode; filter: string },
  listener: AppHomeEventListener,
): () => void {
  const entry = createOrGetEntry(view, filter);
  entry.eventListeners.add(listener);
  return () => {
    entry.eventListeners.delete(listener);
    releaseIfIdle(view, filter, entry);
  };
}

/**
 * Sequence number of the last frame dispatched on this stream: a cursor for
 * `readAppHomeEventsSince`. Every frame a listener receives advances it by one
 * (a server `ping` and a dropped frame do not).
 */
export function getAppHomeStreamSeq({ view, filter }: { view: ViewMode; filter: string }): number {
  return histories.get(makeKey(view, filter))?.seq ?? 0;
}

/**
 * The frames dispatched on this stream after `since` (a `getAppHomeStreamSeq`
 * cursor), oldest first, each with the meta its listeners got. A consumer that
 * was not subscribed for a while (unmounted, or mounted but not yet past its
 * passive effect) reads what it missed here. `null` means a frame after `since`
 * has left the bounded buffer, so the consumer must resync as on a reconnect.
 *
 * It covers only frames this tab received. Frames the server sent while no
 * entry was open are not here (see the known gap on `subscribeAppHomeEvents`).
 */
export function readAppHomeEventsSince(
  { view, filter }: { view: ViewMode; filter: string },
  since: number,
): AppHomeBufferedFrame[] | null {
  const history = histories.get(makeKey(view, filter));
  if (!history) return [];
  if (since < history.evictedThrough) return null;
  return history.frames.filter((frame) => frame.seq > since).map(({ event, meta }) => ({ event, meta }));
}

function getSnapshot(view: ViewMode, filter: string): StreamState {
  // IMPORTANT: getSnapshot must be pure and return stable identity when unchanged
  const entry = registry.get(makeKey(view, filter));
  return entry ? entry.state : DEFAULT_STATE;
}

function getServerSnapshot(): StreamState { return DEFAULT_STATE; }

export function useAppHomeStream({ view, filter }: { view: ViewMode; filter: string }): StreamState {
  // Stabilize subscribe/getSnapshot function identities to avoid unnecessary resubscribe cycles
  const subscribeFn = useCallback((cb: () => void) => subscribe(view, filter, cb), [view, filter]);
  const getSnapshotFn = useCallback(() => getSnapshot(view, filter), [view, filter]);
  const getServerSnapshotFn = useCallback(() => getServerSnapshot(), []);
  return useSyncExternalStore(subscribeFn, getSnapshotFn, getServerSnapshotFn);
}

